// @ts-nocheck
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks/useRedux";
import {
  fetchAllPublicPosts,
  fetchUserOwnerProfile,
  updateAthleticInfo,
} from "../store/postsSlice";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styled from "styled-components";
import { BASE_URL } from "../Constants/BaseUrl";
import ProfilePrivacyToggle from "./ProfilePrivacyToggle";
import { toggleProfileVisibility } from "../store/userSlice";

const genderOptions = [
  { label: "Male", value: "Male" },
  { label: "Female", value: "Female" },
];

const schoolYearOptions = [
  { label: "Freshman", value: "Freshman" },
  { label: "Sophomore", value: "Sophomore" },
  { label: "Junior", value: "Junior" },
  { label: "Senior", value: "Senior" },
];

const divisionOptions = [
  { label: "D1", value: "D1" },
  { label: "D2", value: "D2" },
  { label: "D3", value: "D3" },
  { label: "NAIA", value: "NAIA" },
  { label: "Junior College", value: "Junior College" },
];

const EditProfileModal = ({ isOpen, onClose, userDetails, color }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const [form, setForm] = useState({
    username: "",
    first_name: "",
    last_name: "",
    dob: "",
    gender: "",
    weight: "",
    height: "",
    current_school: "",
    sport_id: "",
    position: "",
    school_year: "",
    division: "",
  });
  const [prevHeight, setPrevHeight] = useState("");
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const visibility = useAppSelector((state) => state.user.profileVisibility);
  const isPrivate = visibility === "private";

  useEffect(() => {
    if (userDetails) {
      const capitalized = userDetails?.athlete_info?.gender
        ? userDetails.athlete_info.gender.charAt(0).toUpperCase() +
          userDetails.athlete_info.gender.slice(1)
        : "";
      setForm({
        username: userDetails?.username || "",
        first_name: userDetails?.athlete_info?.first_name || "",
        last_name: userDetails?.athlete_info?.last_name || "",
        dob: userDetails?.athlete_info?.dob || "",
        gender: capitalized,
        weight: userDetails?.athlete_info?.weight || "",
        height: userDetails?.athlete_info?.height || "",
        current_school: userDetails?.athlete_info?.current_school || "",
        sport_id: userDetails?.athlete_info?.sport_id || "",
        position: userDetails?.athlete_info?.position || "",
        school_year: userDetails?.athlete_info?.school_year || "",
        division: userDetails?.athlete_info?.division || "",
      });
      setPrevHeight(userDetails?.athlete_info?.height || "");
    }
  }, [userDetails]);

  const handleToggle = async () => {
    if (toggleLoading) return;

    setToggleLoading(true);
    const wasPrivate = visibility === "private";

    try {
      // This now returns "public" | "private"
      const newVisibility = await dispatch(toggleProfileVisibility()).unwrap();

      // Refresh profile & posts
      await Promise.all([
        dispatch(fetchUserOwnerProfile(userDetails?.user_id)),
        dispatch(fetchAllPublicPosts()),
      ]);

      // Toast from component = works perfectly & dismisses correctly
      toast.success(
        newVisibility === "private"
          ? "Profile is now PRIVATE"
          : "Profile is now PUBLIC",
        {
          position: "top-right",
          autoClose: 2500,
        }
      );

      onClose();
      navigate("/feed");
    } catch (error: any) {
      toast.error(error || "Failed to update profile visibility", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setToggleLoading(false);
    }
  };
  useEffect(() => {
    axios
      .get(`${BASE_URL}sports/all`, {
        headers: {
          Accept: "application/json",
        },
      })
      .then((response) => {
        console.log(response.data);
        setSports(response.data.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleChangeForHeight = (text) => {
    let cleaned = text.replace(/[^0-9']/g, "");

    if (
      cleaned.length === 1 &&
      prevHeight.length < cleaned.length &&
      !cleaned.includes("'")
    ) {
      cleaned = `${cleaned}'`;
    }

    const parts = cleaned.split("'");
    if (parts.length > 2) {
      return;
    }

    if (parts.length === 2) {
      parts[1] = parts[1].slice(0, 2);
      cleaned = parts[0] + "'" + parts[1];
    }

    setPrevHeight(cleaned);
    setForm((prev) => ({ ...prev, height: cleaned }));
  };

  const validateForm = () => {
    for (const key in form) {
      if (!form[key]) {
        toast.error(`Please fill out the ${key} field.`);
        return false;
      }
    }
    return true;
  };

  const handleSaveDetails = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const { username, ...formWithoutUsername } = form;
      const payload = {
        ...formWithoutUsername,
        fcm_token: "",
        athlete_id: user.user_id,
      };

      console.log("Payload:", payload);

      const result = await dispatch(updateAthleticInfo(payload)).unwrap();

      console.log("Returned API Data:", result);

      onClose();
      // navigate("/welcome", { state: { userInfo: result } });

      toast.success("Profile saved successfully");
    } catch (error) {
      console.error(error);
      toast.error(error ? error : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80">
      {/* <ToastContainer /> */}
      <div
        className="
          bg-gray-900 text-white rounded-2xl shadow-lg
          w-full max-w-lg p-6
          max-h-[100vh] overflow-y-auto
          mx-4 my-4
        "
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Edit Your Profile</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          {[
            { key: "first_name", placeholder: "First Name" },
            { key: "last_name", placeholder: "Last Name" },
            { key: "weight", placeholder: "Weight (LBS)", numeric: true },
            { key: "current_school", placeholder: "Current School" },
            { key: "position", placeholder: "Position" },
            { key: "username", placeholder: "Username" },
          ].map((field) => (
            <input
              key={field.key}
              type={field.numeric ? "number" : "text"}
              className="w-full px-4 py-2 border border-gray-600 bg-gray-800 rounded-md placeholder-gray-400 text-white"
              placeholder={field.placeholder}
              value={form[field.key]}
              onChange={(e) => handleChange(field.key, e.target.value)}
            />
          ))}

          <input
            type="text"
            className="w-full px-4 py-2 border border-gray-600 bg-gray-800 rounded-md placeholder-gray-400 text-white"
            placeholder="Height (e.g., 5'11)"
            value={form.height}
            onChange={(e) => handleChangeForHeight(e.target.value)}
          />

          <StyledDateInput
            type="date"
            className="w-full px-4 py-2 border border-gray-600 bg-gray-800 rounded-md text-white"
            value={form.dob}
            onChange={(e) => handleChange("dob", e.target.value)}
          />

          <select
            className="w-full px-4 py-2 border border-gray-600 bg-gray-800 text-white rounded-md"
            value={form.gender}
            onChange={(e) => handleChange("gender", e.target.value)}
          >
            <option value="" disabled hidden>
              Select Gender
            </option>
            {genderOptions.map((opt) => (
              <option key={opt.value} value={opt.value} className="text-black">
                {opt.label}
              </option>
            ))}
          </select>

          <select
            className="w-full px-4 py-2 border border-gray-600 bg-gray-800 text-white rounded-md"
            value={form.sport_id}
            onChange={(e) => handleChange("sport_id", e.target.value)}
          >
            <option value="" disabled hidden>
              Select Sport
            </option>
            {sports
              .slice()
              .sort((a, b) => a.sport_name.localeCompare(b.sport_name))
              .map((opt) => (
                <option key={opt.id} value={opt.id} className="text-black">
                  {opt.sport_name}
                </option>
              ))}
          </select>

          <select
            className="w-full px-4 py-2 border border-gray-600 bg-gray-800 text-white rounded-md"
            value={form.division}
            onChange={(e) => handleChange("division", e.target.value)}
          >
            <option value="" disabled hidden>
              Division
            </option>
            {divisionOptions.map((opt) => (
              <option key={opt.value} value={opt.value} className="text-black">
                {opt.label}
              </option>
            ))}
          </select>

          <select
            className="w-full px-4 py-2 border border-gray-600 bg-gray-800 text-white rounded-md"
            value={form.school_year}
            onChange={(e) => handleChange("school_year", e.target.value)}
          >
            <option value="" disabled hidden>
              Select School Year
            </option>
            {schoolYearOptions.map((opt) => (
              <option key={opt.value} value={opt.value} className="text-black">
                {opt.label}
              </option>
            ))}
          </select>

          <button
            onClick={handleSaveDetails}
            disabled={loading}
            className="w-full py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            {loading ? "Saving..." : "Save Details"}
          </button>
          <ProfilePrivacyToggle
            toggleLoading={toggleLoading}
            isPrivate={isPrivate}
            onToggle={handleToggle}
            // enabled={true}
            enabled={!toggleLoading}
            color={color}
          />
          <button
            onClick={() => {
              onClose();
              navigate("/change-password-user", {
                state: { email: user.email, isApp: true },
              });
            }}
            disabled={loading}
            className="w-full py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Change Password
          </button>
          <button
            onClick={() => {
              onClose();
              navigate("/logout");
            }}
            disabled={loading}
            className="w-full py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Logout
          </button>
          <button
            onClick={() => {
              onClose();
              navigate("/blocked-users");
            }}
            disabled={loading}
            className="w-full py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Blocked Users
          </button>
          <button
            onClick={() => {
              onClose();
              navigate("/delete-account");
            }}
            disabled={loading}
            className="w-full py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );
};

const StyledDateInput = styled.input`
  &[type="date"]::-webkit-calendar-picker-indicator {
    filter: invert(100%);
  }
`;

export default EditProfileModal;
